/**
 * Permission Registry — canonical resource:action vocabulary.
 *
 * Mirrors shared/accesszen/accesszen/core/registry.py — keep in sync.
 *
 * Naming convention:
 *   resource = "<agent>:<domain>"     e.g. "spend:query", "risk:report"
 *   action   = "read" | "write" | "execute" | "delete" | "admin"
 *
 * Platform-wide resources use "platform:" prefix.
 * Agent-specific resources use "<agent-name>:" prefix.
 * Agent enablement uses "agent:<agent-name>" prefix (Casbin per-user policies).
 */

// ── Platform-wide resources ───────────────────────────────────────────────

export const PLATFORM_SETTINGS = 'platform:settings' as const
export const PLATFORM_USERS = 'platform:users' as const
export const PLATFORM_ROLES = 'platform:roles' as const
export const PLATFORM_WORKSPACE = 'platform:workspace' as const
// ── Developer-gated platform resources ────────────────────────────────────

export const PLATFORM_DEVELOPER = 'platform:developer' as const
export const PLATFORM_CONFIG_SECRET = 'platform:config_secret' as const

// ── Owner-only platform resources ─────────────────────────────────────────

export const PLATFORM_AUDIT = 'platform:audit' as const
// platform:tenant is granted to org:admin (G2), not platform_admin/
// platform_developer (G1) — reclassified from A1 during the drift-guard
// audit (ASC-5643 follow-up). See RESOURCE_CLASS below.
export const PLATFORM_TENANT = 'platform:tenant' as const

// Org lifecycle (A1, RBAC v2 T2 / ASC-5643) — registry-completeness mirror of
// the Python side; enforcement stays require_platform_admin() (g3
// presence-check), this is not a new gate.
export const PLATFORM_ORG = 'platform:org' as const

// ── Member-level platform resources ───────────────────────────────────────

export const PLATFORM_DOCS = 'platform:docs' as const

// ── Analytics resources ───────────────────────────────────────────────────

export const PLATFORM_ANALYTICS = 'platform:analytics' as const
// Note: ANALYTICS_READ/ADMIN share values with the generic READ/ADMIN action
// constants below. They exist as analytics-scoped aliases for call-site clarity.
export const ANALYTICS_READ = 'read' as const
export const ANALYTICS_ADMIN = 'admin' as const

// ── Notification platform resource ───────────────────────────────────────

export const PLATFORM_NOTIFICATIONS = 'platform:notifications' as const

// ── zenflows platform resources (admin-class; Epic ASC-5338) ─────────────
// platform:zenflows (read) / platform:zenflows:configure (write) — both
// org:member and above. No platform:zenflows:govern resource — no governance
// surface in this PRID. Mirrors shared/accesszen/accesszen/core/registry.py.

export const PLATFORM_ZENFLOWS = 'platform:zenflows' as const
export const PLATFORM_ZENFLOWS_CONFIGURE = 'platform:zenflows:configure' as const

// zensources external-API registry (admin-class; Epic ASC-7373).
// org:admin only, read + write. Deliberately NOT platform:config_secret — that
// gates revealing a stored secret's plaintext and is broader than "manage
// integrations"; conflating them blocks least-privilege later (OQ-4). Grant
// breadth matches platform:settings (org_admin-only) so the catalog is never
// visible to a role that cannot open the settings page hosting it.
// Python source of truth: shared/accesszen/accesszen/core/registry.py
export const PLATFORM_EXTERNAL_SOURCE = 'platform:external_source' as const

// zentools configuration (admin-class; ASC-7948). org_admin only, read + write.
// Gates the Tools Registry WRITE surface — a tool's config overlay (PATCH) and
// its operator-configurable settings (PUT .../config): model routing today, and
// from ASC-7876 the org-authored classification rules injected into the
// classifier's prompt. Both routes previously carried authentication only.
// The READ side stays on PLATFORM_SETTINGS/READ, which is what the Tools
// Registry nav item in platform-settings-registry.ts already gates on — so the
// CSV's `read` grant on this resource is unchecked by design, exactly as
// PLATFORM_EXTERNAL_SOURCE's is. Do not "tidy" either away: the row is what a
// later read-side split needs, and roles-data-csv-parity.test.ts ties it to the
// READ entry in roles-data.ts.
// Python source of truth: shared/accesszen/accesszen/core/registry.py
export const PLATFORM_ZENTOOL_CONFIG = 'platform:zentool_config' as const

// ── Workspace data resources ──────────────────────────────────────────────

export const WORKSPACE_DATA = 'workspace:data' as const

// ── Config resources ──────────────────────────────────────────────────────

export const CONFIG_SETTINGS = 'config:settings' as const

// ── Spend Analyzer ────────────────────────────────────────────────────────

export const SPEND_QUERY = 'spend:query' as const
// spend:report retired by ASC-6392 — the Reports surface had zero routes and
// zero UI, so its policy rows and catalog entries were removed then; the
// constant outlived them until ASC-7957.
export const SPEND_DASHBOARD = 'spend:dashboard' as const
export const SPEND_METRIC = 'spend:metric' as const // saved metrics library (CRUD + global)
export const SPEND_SECTION = 'spend:section' as const // dashboard sections (workspace-wide write)
export const SPEND_PRESET = 'spend:preset' as const // preset prompts (admin CRUD)

// Spend Analyzer fine-grained RBAC resources (ASC-6000) — all A3 (agent-gated).
// Module→feature→action catalog lives in spend-permission-catalog.ts.
export const SPEND_WIDGET = 'spend:widget' as const // dashboard widgets
export const SPEND_INSIGHT = 'spend:insight' as const // insights surface (view + feedback)
export const SPEND_SYSTEM_PROMPT = 'spend:system_prompt' as const // system-prompts settings (admin)
export const SPEND_LLM = 'spend:llm' as const // LLM model settings (admin)
export const SPEND_SECURITY = 'spend:security' as const // security settings (admin)
export const SPEND_USERS = 'spend:users' as const // user access management — assign roles (admin)
export const SPEND_ROLE = 'spend:role' as const // roles & permissions mapping surface (admin)
export const SPEND_ONBOARDING = 'spend:onboarding' as const // onboarding-flow preset queries (admin, ASC-6392)
export const SPEND_FILTER = 'spend:filter' as const // saved + live dashboard filters (ASC-10888)

// ── Risk Intelligence ─────────────────────────────────────────────────────
// RISK_REPORT backs dashboard/insights, RISK_SCORE the suppliers surfaces,
// RISK_ALERT the alerts surfaces. Extended for the remaining SRI V2 route
// groups (ASC-6101). RISK_DATASOURCE is the org-scoped exception (FR-059) —
// /data-sources/feed-config has no workspace_id segment.

export const RISK_REPORT = 'risk:report' as const
export const RISK_SCORE = 'risk:score' as const
export const RISK_ALERT = 'risk:alert' as const
export const RISK_TRACKED = 'risk:tracked' as const
export const RISK_BUSINESS_UNIT = 'risk:business_unit' as const
export const RISK_POLICY = 'risk:policy' as const
export const RISK_UPLOAD = 'risk:upload' as const
export const RISK_CHAT = 'risk:chat' as const
export const RISK_DIGEST = 'risk:digest' as const
export const RISK_DATASOURCE = 'risk:datasource' as const

// ── Data Enrichment ───────────────────────────────────────────────────────
// enrichment:pipeline / enrichment:entity retired (T1, ASC-5642) — de:enrichment
// is the sole live resource since T3's org-domain re-homing (ASC-5644).

export const DE_ENRICHMENT = 'de:enrichment' as const

// ── Category Management ───────────────────────────────────────────────────

export const CATEGORY_RESEARCH = 'category:research' as const
export const CATEGORY_BENCHMARK = 'category:benchmark' as const

// ── Category Research Agent (ASC-5994) ────────────────────────────────────
// cra:*-prefixed to stay unambiguous against the separate category-management
// (benchmarking) agent above. Service-local mirror:
// services/category_research_agent/category_research_agent/permissions.py.

export const CRA_SESSION = 'cra:session' as const
export const CRA_REPORT = 'cra:report' as const
export const CRA_FINDINGS = 'cra:findings' as const
export const CRA_SECTION = 'cra:section' as const
export const CRA_FEEDBACK = 'cra:feedback' as const
export const CRA_SHARE = 'cra:share' as const
export const CRA_PRESET = 'cra:preset' as const
export const CRA_PROVIDER = 'cra:provider' as const

// ── RFP Agent (ASC-4587) ──────────────────────────────────────────────────
// Service-local mirror: services/rfp_agent/rfp_agent/permissions.py.
// rfp:artifact is granted in default_policies.csv but has no route behind it
// yet — classified because the grant is real, not because a caller exists.

export const RFP_EVENT = 'rfp:event' as const
export const RFP_PLAN = 'rfp:plan' as const
export const RFP_DOCUMENT = 'rfp:document' as const
export const RFP_ARTIFACT = 'rfp:artifact' as const
export const RFP_PRESET = 'rfp:preset' as const

// ── Partner Assessment (Epic ASC-10167, PRID §9) ──────────────────────────
// Domain partner-assessment. ppa:evaluation is org-domain (org_admin / data_admin,
// DEC-10). Mirror of shared/accesszen/accesszen/core/registry.py.

export const PPA_ASSESSMENT = 'ppa:assessment' as const
export const PPA_EVIDENCE = 'ppa:evidence' as const
export const PPA_REVIEW = 'ppa:review' as const
export const PPA_WORKPRODUCT = 'ppa:workproduct' as const
export const PPA_AUDIT = 'ppa:audit' as const
export const PPA_CONFIG = 'ppa:config' as const
export const PPA_EVALUATION = 'ppa:evaluation' as const

// ── Invoice Processor (ASC-3485) ──────────────────────────────────────────
// Domain invoice-processor, agent_user + agent_admin. invoice:review and
// invoice:writeback each carry a single action today (read / retry); classified
// here because the grant is real, not because the surface is complete (ASC-7957).

export const INVOICE_QUEUE = 'invoice:queue' as const
export const INVOICE_REVIEW = 'invoice:review' as const
export const INVOICE_WRITEBACK = 'invoice:writeback' as const
export const INVOICE_CHAT = 'invoice:chat' as const
export const INVOICE_SETTINGS = 'invoice:settings' as const
export const INVOICE_DASHBOARD = 'invoice:dashboard' as const

// ── Contract Compliance ───────────────────────────────────────────────────

export const CONTRACT_COMPLIANCE = 'contract:compliance' as const
export const CONTRACT_ALERT = 'contract:alert' as const
export const CONTRACT_PRESET = 'contract:preset' as const

// ── Orchestrator ──────────────────────────────────────────────────────────

export const ORCHESTRATOR_QUERY = 'orchestrator:query' as const

// ── Materials Intelligence (ASC-5756) ─────────────────────────────────────
/** Dashboard / materials-data surface (provisional — no backend yet) */
export const MATERIALS_QUERY = 'materials:query' as const

// ── configzen (Platform Configuration) ───────────────────────────────

export const CONFIG_SECRETS = 'config:secrets' as const
export const CONFIG_AUDIT = 'config:audit' as const
// Operational STABILITY knobs — concurrency caps, workflow timeouts, rate limits
// (ASC-9225). platform_developer only. Split out of CONFIG_SETTINGS, which
// org_admin AND data_admin hold, so a data_admin could stall their own platform.
// Membership is per-key on configzen's `KeyPolicy.operational`, not a list here.
// NOT infra topology (postgres_url, redis_url) — that is env-only, no role reaches it.
export const CONFIG_INFRA = 'config:infra' as const

// ── Promptzen resources ───────────────────────────────────────────────────

/** Mirrors PROMPTZEN_PROMPT in shared/accesszen/accesszen/core/registry.py */
export const PROMPTZEN_PROMPT = 'promptzen:prompt' as const
/** Diff-proposal review queue — org:admin in V1 (separate prompt:owner role in V2). */
export const PROMPTZEN_DIFF_REVIEW = 'promptzen:diff_review' as const
/** contextzen + insights module prompts (ASC-10869): org_admin r/w, data_admin read. */
export const PROMPTZEN_MODULE_PROMPT = 'promptzen:module_prompt' as const

// ── LLMRouter resources ───────────────────────────────────────────────────

export const LLMROUTER_MODEL = 'llmrouter:model' as const
export const LLMROUTER_BUDGET = 'llmrouter:budget' as const
export const LLMROUTER_EXPERIMENT = 'llmrouter:experiment' as const
export const LLMROUTER_DEFAULT_MODEL = 'llmrouter:default_model' as const

// ── Contextzen resources ───────────────────────────────────────────────────
// Mirrors shared/accesszen/accesszen/core/registry.py — added to close
// Python-side drift surfaced by the A0-A4 registry-drift guard (registry.py
// already declared these; TS previously only carried the two role constants).

export const CONTEXTZEN_DOMAIN = 'contextzen:domain' as const
export const CONTEXTZEN_VARIABLE = 'contextzen:variable' as const
export const CONTEXTZEN_SCHEMA = 'contextzen:schema' as const
export const CONTEXTZEN_FACT = 'contextzen:fact' as const
export const CONTEXTZEN_SESSION = 'contextzen:session' as const
export const CONTEXTZEN_SOURCE = 'contextzen:source' as const
export const CONTEXTZEN_BINDING = 'contextzen:binding' as const
export const CONTEXTZEN_TAXONOMY = 'contextzen:taxonomy' as const
export const CONTEXTZEN_DICTIONARY = 'contextzen:dictionary' as const
export const CONTEXTZEN_OBJECTIVE = 'contextzen:objective' as const
export const CONTEXTZEN_METADATA = 'contextzen:metadata' as const
export const CONTEXTZEN_REPOSITORY = 'contextzen:repository' as const
export const CONTEXTZEN_PACK = 'contextzen:pack' as const
export const CONTEXTZEN_PROMPT = 'contextzen:prompt' as const

// ── Workplace VLM ───────────────────────────────────────────────────────

export const WCA_SITE = 'wca:site' as const
export const WCA_ALERT = 'wca:alert' as const
export const WCA_CONFIG = 'wca:config' as const
export const WCA_QUERY = 'wca:query' as const

// ── Feedbackzen resources + role (admin-class; ASC-4170) ──────────────────

export const FEEDBACK_EVENT = 'feedback:event' as const
export const FEEDBACK_ITEM = 'feedback:item' as const
export const FEEDBACK_REVIEWER = 'feedback:reviewer' as const
export const FEEDBACK_VIEWER = 'feedback:viewer' as const

// ── Evalzen resources (org-scoped; Epic ASC-4285) ─────────────────────────
// Mirrors EVALZEN_DATASET / EVALZEN_RUN in shared/accesszen/accesszen/core/registry.py

export const EVALZEN_DATASET = 'evalzen:dataset' as const
export const EVALZEN_RUN = 'evalzen:run' as const

// ── Actions ───────────────────────────────────────────────────────────────

export const READ = 'read' as const
export const WRITE = 'write' as const
export const EXECUTE = 'execute' as const
export const DELETE = 'delete' as const
export const ADMIN = 'admin' as const
export const DEVELOPER = 'developer' as const
export const ACCESS = 'access' as const
export const SEND = 'send' as const
// Scoped pipeline runs (ASC-8011). Mirrors `RUN_SCOPE` in
// `accesszen/core/registry.py`. Kept separate from EXECUTE because the two differ in
// blast radius, not in kind: EXECUTE runs a pipeline over its normal input, while a
// scoped run can select `all` and reprocess an org's entire history.
export const RUN_SCOPE = 'run_scope' as const
export const FORWARD = 'forward' as const
export const REJECT = 'reject' as const
export const EDIT = 'edit' as const
export const SHARE = 'share' as const // spend metric library sharing — RBAC v2 T1
export const APPROVE = 'approve' as const // HITL approve/reject — RBAC v2 T1
// Fine-grained business-action vocabulary (ASC-6000 — Spend Analyzer RBAC).
export const CREATE = 'create' as const
export const MANAGE = 'manage' as const
export const EXPORT = 'export' as const

// ── Data Ingestion Agent resources (admin-class, Triad v4.5 §10A) ─────────

/** Pipeline configurations, runs, bulk-review */
export const DI_PIPELINE = 'di:pipeline' as const
/** Recipes + activation */
export const DI_RECIPE = 'di:recipe' as const
/** Extraction profiles + activation */
export const DI_PROFILE = 'di:profile' as const
/** Connectors + sync history + credential management */
export const DI_CONNECTOR = 'di:connector' as const
/** Quarantine rows + bulk-import */
export const DI_QUARANTINE = 'di:quarantine' as const
/** Stage thresholds (data quality config) */
export const DI_QUALITY = 'di:quality' as const
/** Agent settings (configzen-backed) */
export const DI_SETTINGS = 'di:settings' as const
/** CSV + document uploads */
export const DI_UPLOAD = 'di:upload' as const
/** Explorer (read) + extraction-review HITL queue — enforced in CSV; RBAC v2 T1 */
export const DI_EXPLORER = 'di:explorer' as const
export const DI_EXTRACTION_REVIEW = 'di:extraction_review' as const

// ── Insights Agent resources (admin-class, org-domain — ASC-5831) ─────────
// Re-homed from the agent domain (g2 insights:admin/runner/viewer hierarchy)
// to the org domain: org:admin full, org:data_admin run-execute, org:member
// read-only.

/** Insights list / detail / findings / history (read) */
export const INSIGHTS_INSIGHT = 'insights:insight' as const
/** Team registry reads (read) */
export const INSIGHTS_TEAM = 'insights:team' as const
/** Pipeline run lifecycle — generate, cancel (execute — admin/data_admin) */
export const INSIGHTS_RUN = 'insights:run' as const
/** Admin inventory + diagnostics (read — org:admin only) */
export const INSIGHTS_INVENTORY = 'insights:inventory' as const
/** ASC-6518: granular agent x workspace insights scheduler config (read/manage — org:developer+) */
export const INSIGHTS_SCHEDULE = 'insights:schedule' as const

// ── Agent registry (canonical agent names) ────────────────────────────────

export const AGENT_DATA_INGESTION = 'data-ingestion' as const
export const AGENT_SPEND_ANALYZER = 'spend-analyzer' as const
export const AGENT_SUPPLIER_RISK_INTELLIGENCE = 'supplier-risk-intelligence' as const
export const AGENT_CONTRACT_COMPLIANCE = 'contract-compliance' as const
export const AGENT_WORKPLACE_VLM = 'workplace-vlm' as const
export const AGENT_MATERIALS_INTELLIGENCE = 'materials-intelligence' as const
export const AGENT_DATA_ENRICHMENT = 'data-enrichment' as const
export const AGENT_CATEGORY_RESEARCH = 'category-research' as const
export const AGENT_RFP_AGENT = 'rfp-agent' as const
export const AGENT_ORCHESTRATOR = 'orchestrator' as const
export const AGENT_PARTNER_ASSESSMENT = 'partner-assessment' as const
export const AGENT_INSIGHTS = 'insights' as const
export const AGENT_INVOICE_PROCESSOR = 'invoice-processor' as const
// Zen role domains — zens with reviewer-style roles (feedbackzen's
// feedback:reviewer, ASC-4170 FR-014) live in the agent Casbin domain (g2)
// because the dual-domain model has no third "zen" domain. Registered here so
// the Agent Access screen can ALLOCATE these roles — without this entry the
// role exists but no admin surface can grant it. Found in e2e 2026-06-04.
export const ZEN_DOMAIN_FEEDBACK = 'feedback' as const
export const ZEN_DOMAIN_CONTEXTZEN = 'contextzen' as const

/** Build agent resource string for Casbin policies. */
export function agentResource(agentName: string): string {
  return `agent:${agentName}`
}

// ── Resource classification (RBAC v2 — A0–A4 gate taxonomy) ───────────────
// Mirrors RESOURCE_CLASS in shared/accesszen/accesszen/core/registry.py.
// A0 open · A1 platform-admin · A2 org · A3 agent · A4 dual (org ⊕ agent).
// Ref: Epic ASC-5641 / ASC-5642 (T1).

export const CLASS_A0 = 'A0' as const
export const CLASS_A1 = 'A1' as const
export const CLASS_A2 = 'A2' as const
export const CLASS_A3 = 'A3' as const
export const CLASS_A4 = 'A4' as const

export const RESOURCE_CLASS: Record<string, string> = {
  // A1 — platform-admin / Platform Developer tier
  [PLATFORM_ORG]: CLASS_A1,
  // A2 since ASC-9224 — platform_developer is a g2 org role now, enforced by
  // the org gate (domain=org_id). Mirrors the Python registry.
  [PLATFORM_DEVELOPER]: CLASS_A2,
  // A2 — org-gated
  [PLATFORM_SETTINGS]: CLASS_A2,
  // ASC-6759: moved A1 -> A2 with the policy rows (org_admin + data_admin).
  [INSIGHTS_SCHEDULE]: CLASS_A2,
  [PLATFORM_USERS]: CLASS_A2,
  [PLATFORM_ROLES]: CLASS_A2,
  [PLATFORM_WORKSPACE]: CLASS_A2,
  [PLATFORM_AUDIT]: CLASS_A2,
  [PLATFORM_DOCS]: CLASS_A2,
  // platform:tenant and platform:config_secret are granted to org:admin
  // (G2), not platform_admin/platform_developer (G1) — reclassified from
  // A1 during the drift-guard audit (ASC-5643 follow-up).
  [PLATFORM_TENANT]: CLASS_A2,
  [PLATFORM_CONFIG_SECRET]: CLASS_A2,
  [PLATFORM_EXTERNAL_SOURCE]: CLASS_A2,
  [PLATFORM_ZENTOOL_CONFIG]: CLASS_A2,
  [CONFIG_SECRETS]: CLASS_A2,
  [CONFIG_AUDIT]: CLASS_A2,
  [CONFIG_INFRA]: CLASS_A2,
  [WORKSPACE_DATA]: CLASS_A2,
  [LLMROUTER_MODEL]: CLASS_A2,
  [LLMROUTER_BUDGET]: CLASS_A2,
  [LLMROUTER_EXPERIMENT]: CLASS_A2,
  [FEEDBACK_EVENT]: CLASS_A2,
  [FEEDBACK_ITEM]: CLASS_A2,
  [EVALZEN_DATASET]: CLASS_A2,
  [EVALZEN_RUN]: CLASS_A2,
  [PLATFORM_ZENFLOWS]: CLASS_A2,
  [PLATFORM_ZENFLOWS_CONFIGURE]: CLASS_A2,
  [DI_PIPELINE]: CLASS_A2,
  [DI_RECIPE]: CLASS_A2,
  [DI_PROFILE]: CLASS_A2,
  [DI_CONNECTOR]: CLASS_A2,
  [DI_QUARANTINE]: CLASS_A2,
  [DI_QUALITY]: CLASS_A2,
  [DI_SETTINGS]: CLASS_A2,
  [DI_UPLOAD]: CLASS_A2,
  [DI_EXPLORER]: CLASS_A2,
  [DI_EXTRACTION_REVIEW]: CLASS_A2,
  [DE_ENRICHMENT]: CLASS_A2,
  [PLATFORM_NOTIFICATIONS]: CLASS_A2,
  // promptzen:diff_review is org:admin-only today (no agent-domain grant
  // exists for it) — distinct from promptzen:prompt, which is genuinely
  // dual-gated. Reclassified from A4.
  [PROMPTZEN_DIFF_REVIEW]: CLASS_A2,
  [PROMPTZEN_MODULE_PROMPT]: CLASS_A2,
  // Insights re-homed to the org domain (ASC-5831) — was A3 pending this
  // landing. org:admin full, org:data_admin run-execute, org:member read-only.
  [INSIGHTS_INSIGHT]: CLASS_A2,
  [INSIGHTS_TEAM]: CLASS_A2,
  [INSIGHTS_RUN]: CLASS_A2,
  // Partner Assessment QA gate (ASC-10180, DEC-10): org_admin + data_admin.
  [PPA_EVALUATION]: CLASS_A2,
  [INSIGHTS_INVENTORY]: CLASS_A2,
  // A3 — agent-gated (the named data agents' own product surfaces)
  [SPEND_QUERY]: CLASS_A3,
  [SPEND_DASHBOARD]: CLASS_A3,
  [SPEND_METRIC]: CLASS_A3,
  [SPEND_SECTION]: CLASS_A3,
  [SPEND_PRESET]: CLASS_A3,
  // Spend Analyzer fine-grained RBAC resources (ASC-6000) — all agent-gated.
  [SPEND_WIDGET]: CLASS_A3,
  [SPEND_INSIGHT]: CLASS_A3,
  [SPEND_SYSTEM_PROMPT]: CLASS_A3,
  [SPEND_LLM]: CLASS_A3,
  [SPEND_SECURITY]: CLASS_A3,
  [SPEND_USERS]: CLASS_A3,
  [SPEND_ROLE]: CLASS_A3,
  [SPEND_ONBOARDING]: CLASS_A3,
  [SPEND_FILTER]: CLASS_A3,
  [RISK_REPORT]: CLASS_A3,
  [RISK_SCORE]: CLASS_A3,
  [RISK_ALERT]: CLASS_A3,
  [RISK_TRACKED]: CLASS_A3,
  [RISK_BUSINESS_UNIT]: CLASS_A3,
  [RISK_POLICY]: CLASS_A3,
  [RISK_UPLOAD]: CLASS_A3,
  [RISK_CHAT]: CLASS_A3,
  [RISK_DIGEST]: CLASS_A3,
  [RISK_DATASOURCE]: CLASS_A3,
  [WCA_SITE]: CLASS_A3,
  [WCA_ALERT]: CLASS_A3,
  [WCA_CONFIG]: CLASS_A3,
  [WCA_QUERY]: CLASS_A3,
  [CATEGORY_RESEARCH]: CLASS_A3,
  [CATEGORY_BENCHMARK]: CLASS_A3,
  // Category Research Agent product surfaces (ASC-5994) — domain
  // category-research, granted to agent_user/agent_admin.
  [CRA_SESSION]: CLASS_A3,
  [CRA_REPORT]: CLASS_A3,
  [CRA_FINDINGS]: CLASS_A3,
  [CRA_SECTION]: CLASS_A3,
  [CRA_FEEDBACK]: CLASS_A3,
  [CRA_SHARE]: CLASS_A3,
  [CRA_PRESET]: CLASS_A3,
  [CRA_PROVIDER]: CLASS_A3,
  // RFP Agent product surfaces (ASC-4587) — domain rfp-agent.
  [RFP_EVENT]: CLASS_A3,
  [RFP_PLAN]: CLASS_A3,
  [RFP_DOCUMENT]: CLASS_A3,
  [RFP_ARTIFACT]: CLASS_A3,
  [RFP_PRESET]: CLASS_A3,
  // Partner Assessment product surfaces (ASC-10180) — domain partner-assessment.
  [PPA_ASSESSMENT]: CLASS_A3,
  [PPA_EVIDENCE]: CLASS_A3,
  [PPA_REVIEW]: CLASS_A3,
  [PPA_WORKPRODUCT]: CLASS_A3,
  [PPA_AUDIT]: CLASS_A3,
  [PPA_CONFIG]: CLASS_A3,
  // Invoice Processor product surfaces (ASC-3485) — domain invoice-processor.
  [INVOICE_QUEUE]: CLASS_A3,
  [INVOICE_REVIEW]: CLASS_A3,
  [INVOICE_WRITEBACK]: CLASS_A3,
  [INVOICE_CHAT]: CLASS_A3,
  [INVOICE_SETTINGS]: CLASS_A3,
  [INVOICE_DASHBOARD]: CLASS_A3,
  [CONTRACT_COMPLIANCE]: CLASS_A3,
  [CONTRACT_ALERT]: CLASS_A3,
  [CONTRACT_PRESET]: CLASS_A3,
  // A4 since ASC-11281: org_normal (org domain) OR agent_user (agent domain).
  [ORCHESTRATOR_QUERY]: CLASS_A4,
  [MATERIALS_QUERY]: CLASS_A3,
  // Tier B1 agent default model (ASC-5934) — agent_admin-only, per-agent; no
  // org-domain grant (distinct from the org-gated LLMROUTER_MODEL allowlist).
  [LLMROUTER_DEFAULT_MODEL]: CLASS_A3,
  // A4 — dual-gated (org ⊕ agent: prompts, configs, observability)
  [CONFIG_SETTINGS]: CLASS_A4,
  [PROMPTZEN_PROMPT]: CLASS_A4,
  [PLATFORM_ANALYTICS]: CLASS_A4,
  // contextzen is a platform module, not an agent: every contextzen:* resource
  // is org-domain only (was A3/A4 via the retired `contextzen` agent domain).
  // Ref: ASC-10869.
  [CONTEXTZEN_DOMAIN]: CLASS_A2,
  [CONTEXTZEN_VARIABLE]: CLASS_A2,
  [CONTEXTZEN_SCHEMA]: CLASS_A2,
  [CONTEXTZEN_FACT]: CLASS_A2,
  [CONTEXTZEN_BINDING]: CLASS_A2,
  [CONTEXTZEN_TAXONOMY]: CLASS_A2,
  [CONTEXTZEN_DICTIONARY]: CLASS_A2,
  [CONTEXTZEN_OBJECTIVE]: CLASS_A2,
  [CONTEXTZEN_SESSION]: CLASS_A2,
  [CONTEXTZEN_SOURCE]: CLASS_A2,
  [CONTEXTZEN_REPOSITORY]: CLASS_A2,
  [CONTEXTZEN_METADATA]: CLASS_A2,
  [CONTEXTZEN_PACK]: CLASS_A2,
  [CONTEXTZEN_PROMPT]: CLASS_A2,
}

/** Return the A0–A4 gate class for a resource (A0 if unclassified/open). */
export function resourceClass(resource: string): string {
  return RESOURCE_CLASS[resource] ?? CLASS_A0
}

// ── Agent role constants (g3 grouping) ────────────────────────────────────

// All named agents share these two roles, scoped by domain (agent_name) —
// disambiguation is the Casbin domain column, not the role string. The
// per-agent-prefixed roles below (spend:admin, wca:admin, di:admin, …) never
// matched a real g3 row (default_policies.csv has only ever seeded
// agent_admin/agent_user) and are retired as dead constants. Ptype renamed
// g2->g3 + role strings normalized to snake_case (ASC-5834). Mirrors
// AGENT_ADMIN/AGENT_USER in shared/accesszen/accesszen/core/registry.py —
// keep in sync (see the "generic vs per-agent-prefixed roles" design
// decision recorded on ASC-5834 / the RBAC design-spec Linear document).
export const AGENT_ADMIN = 'agent_admin' as const
export const AGENT_USER = 'agent_user' as const

// ── Agent role registry (agent_name → ordered roles highest→lowest) ──────
// Mirrors AGENT_ROLES in registry.py. ASC-5851: AGENT_DATA_INGESTION,
// AGENT_DATA_ENRICHMENT, and the feedback zen-domain entry removed — all
// three were fully re-homed to the org domain by T3 (ASC-5644); no g3 CSV
// row exists for any of them, so keeping them here surfaced a dead
// per-agent role picker on the Agent Access screen that silently granted a
// Casbin role no route ever checks. ASC-10869: AGENT_INSIGHTS and
// ZEN_DOMAIN_CONTEXTZEN removed for the same reason — platform modules
// administered through org roles, not agents.

export const AGENT_ROLES: Record<string, readonly string[]> = {
  [AGENT_SPEND_ANALYZER]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_SUPPLIER_RISK_INTELLIGENCE]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_WORKPLACE_VLM]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_MATERIALS_INTELLIGENCE]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_CATEGORY_RESEARCH]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_CONTRACT_COMPLIANCE]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_ORCHESTRATOR]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_RFP_AGENT]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_PARTNER_ASSESSMENT]: [AGENT_ADMIN, AGENT_USER],
  [AGENT_INVOICE_PROCESSOR]: [AGENT_ADMIN, AGENT_USER],
} as const
